/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Card, Table, Spinner, Button, Modal, Row, Col } from "react-bootstrap";
import Chart from "react-apexcharts";
import { InfiniteScroll } from "../../../components";
import { CustomSelect } from "../../../custom/Select/CustomSelect";
import { getQuantityTypeLabel } from "../../../../utils";
import APICallService from "../../../../api/apiCallService";
import { manageProduct } from "../../../../api/apiEndPoints";
import { fetchActivityLogs } from "../../activity-logs/services/activity-log.service";
import { IActivityLog } from "../../activity-logs/types/activity-log.types";
import {
  PriceCatalog,
  Pricing,
  QuantityType,
} from "../../../../types";
import { Response } from "../../../../types/response_data";
import { AuditLogFilterData } from "../../../../types/price_catalog";


const PRICE_CATALOG_PLATFORM_VALUE = 2;
const PRICE_CATALOG_ACTIVITY_LOG_TYPE = "price_catalog";
const PAGE_LIMIT = 50;

export interface GraphPayload {
  active: boolean;
  warehouseId: string;
  defaultWarehouse: boolean;
  createdBy: string;
  quantityTypes: QuantityType[];
  createdAt: string;
}

export const ChangeHistoryTab = ({
  warehouseId,
}: {
  warehouseId: string | undefined;
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [records, setRecords] = useState<IActivityLog[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);

  // Graph Modal States
  const [showGraphModal, setShowGraphModal] = useState(false);
  const [graphLoading, setGraphLoading] = useState(false);
  const [graphPayload, setGraphPayload] = useState<Pricing[]>([]);

  // ApexCharts States
  const [chartSeries, setChartSeries] = useState<any[]>([]);
  const [chartOptions, setChartOptions] = useState<any>({});

  // Audit Log Filter Details
  const [auditLogFilterDetails, setAuditLogFilterDetails] =
    useState<AuditLogFilterData | null>(null);

  // Filter selections: Product (variant) and User
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [selectedUser, setSelectedUser] = useState<any>(null);

  // Fetch filter options once on mount
  useEffect(() => {
    fetchAuditLogFilterDetails();
  }, []);

  // Refetch history whenever the Product/User filters change (also covers the initial load)
  useEffect(() => {
    fetchHistory(0, false);
  }, [selectedProduct, selectedUser]);

  // Fetch audit log filter details once for this warehouse
  const fetchAuditLogFilterDetails = async () => {
    try {
      const apiService = new APICallService(
        manageProduct.getPriceCatalogEntryForAuditLogFilter,
        "",
        "",
        "",
        "",
      );
      const response: Response<AuditLogFilterData> = await apiService.callAPI();
      if (response.status === "success") {
        setAuditLogFilterDetails(response?.data || null);
      }
    } catch (error) {
      console.error("Failed to fetch audit log filter details", error);
      setAuditLogFilterDetails(null);
    }
  };

  const fetchHistory = async (skip: number, append: boolean) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const response: any = await fetchActivityLogs({
        pageNo: Math.floor(skip / PAGE_LIMIT) + 1,
        limit: PAGE_LIMIT,
        platform: PRICE_CATALOG_PLATFORM_VALUE,
        type: PRICE_CATALOG_ACTIVITY_LOG_TYPE,
        skip,
        searchTerm: "",
        variantId: selectedProduct?.value,
        userId: selectedUser?.value,
      });
      if (response?.records) {
        setRecords((prev) =>
          append ? [...prev, ...response.records] : response.records,
        );
        setTotalRecords(response.total || 0);
      } else if (!append) {
        setRecords([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error("Failed to fetch change history", error);
    } finally {
      if (append) setLoadingMore(false);
      else setLoading(false);
    }
  };

  const hasMore = !loading && records.length < totalRecords;

  const loadMoreHistory = () => {
    if (loading || loadingMore || !hasMore) return;
    fetchHistory(records.length, true);
  };

  // Open Modal and Fetch Graph Data
  const handleViewGraph = async (recordId?: string) => {
    if (!recordId) return;

    setShowGraphModal(true);
    setGraphLoading(true);

    try {
      const apiService = new APICallService(
        manageProduct.getPriceCatalogEntry,
        `${recordId}?project=${encodeURIComponent(
          JSON.stringify({ pricing: 1 }),
        )}&warehouse=${warehouseId}`,
        "",
        "",
        "",
        "",
      );
      const response: PriceCatalog = await apiService.callAPI();
      const payload = response?.pricing || [];

      setGraphPayload(payload);
    } catch (error) {
      console.error("Failed to fetch graph data", error);
      setGraphPayload([]);
    } finally {
      setGraphLoading(false);
    }
  };

  // Color pairs per quantity type: [CP shade, SP shade], cycled if there are more types
  const QUANTITY_TYPE_COLOR_PAIRS = [
    ["#93c5fd", "#2563eb"], // blue
    ["#fcd34d", "#d97706"], // amber
    ["#6ee7b7", "#059669"], // green
    ["#fca5a5", "#dc2626"], // red
    ["#d8b4fe", "#7c3aed"], // purple
  ];

  // Build the CP/SP trend chart from the price-change history whenever new data arrives
  useEffect(() => {
    if (graphPayload.length === 0) return;

    // Order snapshots chronologically so the trend reads left-to-right
    const sortedEntries = [...graphPayload].sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );

    // Collect every quantity type that appears across all snapshots
    const quantityTypeSet = new Set<number>();
    sortedEntries.forEach((entry) =>
      entry.quantityTypes.forEach((qt) => quantityTypeSet.add(qt.type)),
    );
    const sortedTypes = Array.from(quantityTypeSet).sort((a, b) => a - b);

    const categories = sortedEntries.map((entry) =>
      new Date(entry.createdAt).toLocaleString(undefined, {
        year: "numeric",
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }),
    );

    const series: { name: string; data: (number | null)[] }[] = [];
    const colors: string[] = [];
    const dashArray: number[] = [];

    sortedTypes.forEach((type, index) => {
      const label = getQuantityTypeLabel(type);
      const [cpColor, spColor] =
        QUANTITY_TYPE_COLOR_PAIRS[index % QUANTITY_TYPE_COLOR_PAIRS.length];

      series.push({
        name: `${label} - CP`,
        data: sortedEntries.map(
          (entry) =>
            entry.quantityTypes.find((qt) => qt.type === type)?.costPrice ??
            null,
        ),
      });
      colors.push(cpColor);
      dashArray.push(5);

      series.push({
        name: `${label} - SP`,
        data: sortedEntries.map(
          (entry) =>
            entry.quantityTypes.find((qt) => qt.type === type)?.sellingPrice ??
            null,
        ),
      });
      colors.push(spColor);
      dashArray.push(0);
    });

    setChartSeries(series);

    // Highlight the currently active snapshot, if present, on the x-axis
    const activeIndex = sortedEntries.findIndex((entry) => entry.active);

    // Define ApexCharts configuration
    setChartOptions({
      chart: {
        type: "line",
        toolbar: { show: true },
        fontFamily: "inherit",
        zoom: { enabled: false },
      },
      stroke: {
        width: 3,
        curve: "smooth",
        dashArray,
      },
      colors,
      markers: {
        size: 5,
        hover: { size: 7 },
      },
      xaxis: {
        categories,
        labels: { show: false },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        title: {
          text: "Price (TSH)",
          style: { color: "#6B7280", fontWeight: 600 },
        },
        labels: {
          formatter: (val: number) => (val ? val.toLocaleString() : "0"),
          style: { colors: "#6B7280", fontSize: "13px" },
        },
      },
      tooltip: {
        theme: "light",
        y: {
          formatter: (val: number) =>
            val || val === 0 ? `TSH ${val.toLocaleString()}` : "N/A",
        },
      },
      grid: {
        borderColor: "#E5E7EB",
        strokeDashArray: 3,
        xaxis: { lines: { show: true } },
        yaxis: { lines: { show: true } },
      },
      legend: {
        position: "top",
        horizontalAlign: "center",
        fontSize: "13px",
        itemMargin: { horizontal: 10, vertical: 8 },
        markers: { radius: 12 },
      },
      annotations:
        activeIndex >= 0
          ? {
            xaxis: [
              {
                x: categories[activeIndex],
                borderColor: "#00A76F",
                label: {
                  text: "Current",
                  style: {
                    color: "#fff",
                    background: "#00A76F",
                  },
                },
              },
            ],
          }
          : undefined,
    });
  }, [graphPayload]);

  return (
    <Card className="border bg-f9f9f9 py-3 custom-tabContainer">
      <Card.Header className="border-bottom-0 mb-3 d-flex justify-content-between align-items-center">
        <Card.Title className="fs-22 fw-bolder mb-0">Change History</Card.Title>
      </Card.Header>

      <Card.Body className="p-0 pb-6">
        {/* Filters Section */}
        <Card className="border-0 shadow-sm mb-5 mx-6">
          <Card.Body className="px-7 py-6">
            <Row className="align-items-end gap-5">
              <Col xs={12} sm={12} md={3}>
                <div className="w-100">
                  <label className="form-label fw-bold mb-2">Product</label>
                  <CustomSelect
                    backgroundColor="#ffffff"
                    placeholder="Select product"
                    isSearchable={true}
                    isClearable={true}
                    options={(auditLogFilterDetails?.variants || []).map(
                      (variant) => ({
                        value: variant._id,
                        label: variant.title,
                      }),
                    )}
                    value={selectedProduct}
                    onChange={(option: any) => setSelectedProduct(option)}
                  />
                </div>
              </Col>

              <Col xs={12} sm={12} md={3}>
                <div className="w-100">
                  <label className="form-label fw-bold mb-2">User</label>
                  <CustomSelect
                    backgroundColor="#ffffff"
                    placeholder="Select user"
                    isSearchable={true}
                    isClearable={true}
                    options={(auditLogFilterDetails?.users || []).map(
                      (user) => ({
                        value: user._id,
                        label: user.name,
                      }),
                    )}
                    value={selectedUser}
                    onChange={(option: any) => setSelectedUser(option)}
                  />
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <div className="table-responsive h-lg-400px overflow-y-auto border-top border-bottom bg-white">
          <InfiniteScroll
            hasMore={hasMore}
            loading={loadingMore}
            onLoadMore={loadMoreHistory}
          >
            <Table
              hover
              className="table table-row-bordered align-middle gs-7 gy-4 mb-0"
            >
              <thead className="bg-light">
                <tr className="fs-15 fw-bold text-muted text-uppercase h-60px align-middle">
                  <th className="min-w-300px">Variant name</th>
                  <th className="min-w-150px ps-4">User</th>
                  <th className="min-w-300px">Activity Detail</th>
                  <th className="min-w-150px">Date</th>
                  <th className="min-w-100px text-end pe-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="text-center py-5">
                      <Spinner animation="border" variant="primary" />
                    </td>
                  </tr>
                ) : records.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="text-center py-5 text-muted fw-500"
                    >
                      No change history found.
                    </td>
                  </tr>
                ) : (
                  records.map((record) => (
                    <tr key={record._id}>
                      <td className="fs-14 fw-500 text-muted">
                        {(record.data as any)?.title || ""}
                      </td>
                      <td className="ps-4">
                        <div className="d-flex align-items-center">
                          <div
                            className="symbol symbol-35px bg-primary bg-opacity-10 text-primary fw-bold text-uppercase d-flex justify-content-center align-items-center rounded-circle me-3"
                            style={{ width: "35px", height: "35px" }}
                          >
                            {record.user?.name?.charAt(0) || "U"}
                          </div>
                          <div className="fs-15 fw-600 text-dark">
                            {record.user?.name}
                          </div>
                        </div>
                      </td>
                      <td className="fs-14 fw-500 text-muted">
                        {record.message}
                      </td>
                      <td className="fs-14 fw-500 text-muted">
                        {record._createdAt
                          ? new Date(record._createdAt).toLocaleString(
                            undefined,
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            },
                          )
                          : "-"}
                      </td>
                      <td className="text-end pe-4">
                        <Button className="uw-btn uw-btn-sm"
                          variant="outline-primary"
                          size="sm"
                          disabled={!(record as any)?.data?.recordId}
                          onClick={() =>
                            handleViewGraph((record as any)?.data?.recordId)
                          }
                        >
                          <i className="bi bi-graph-up me-2"></i>
                          View Graph
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </InfiniteScroll>
        </div>
      </Card.Body>

      {/* Price Change Line Graph Modal */}
      <Modal
        show={showGraphModal}
        onHide={() => setShowGraphModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fs-3 fw-bolder">
            Warehouse Price Trends
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2 pb-5 px-5">
          {graphLoading ? (
            <div className="d-flex flex-column align-items-center justify-content-center py-5">
              <Spinner animation="border" variant="primary" className="mb-3" />
              <span className="text-muted fw-500">Loading chart data...</span>
            </div>
          ) : graphPayload.length === 0 ? (
            <div className="alert alert-warning text-center fw-500 border-0 bg-warning bg-opacity-10 text-warning">
              No graph data available for this record.
            </div>
          ) : (
            <>
              {/* Chart Header */}
              <div className="d-flex justify-content-between align-items-end mb-4 bg-light p-3 rounded">
                <div className="text-muted fs-14 fw-500 me-4">
                  This line graph tracks how Cost Price (CP) and Selling Price
                  (SP) changed over time for each quantity type.{" "}
                  <span className="fw-bold text-decoration-underline">
                    Dashed lines
                  </span>{" "}
                  represent CP, and <span className="fw-bold">solid lines</span>{" "}
                  represent SP. Each color pair corresponds to a quantity type.
                </div>
              </div>

              {/* Chart Renderer */}
              <div id="chart" className="mt-4">
                {chartSeries.length > 0 && chartSeries[0].data.length > 0 ? (
                  <Chart
                    options={chartOptions}
                    series={chartSeries}
                    type="line"
                    height={400}
                  />
                ) : (
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ height: "300px" }}
                  >
                    <span className="text-muted fw-500">
                      No quantity price data recorded for this warehouse.
                    </span>
                  </div>
                )}
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>
    </Card>
  );
};
